/**
 * Seed policy for seeded runs (BULLET-HELL.md §D8) — what a seed may look like
 * and which one today's is. The mechanism lives in core/Rng.js; this file owns
 * only the format, so the game, the leaderboard and the server can all agree on
 * what counts as the same seed.
 *
 * The format is deliberately narrow — uppercase A-Z, 0-9 and dashes, 24 chars —
 * for one reason: a seed is a leaderboard key. Two players who typed "melodia"
 * and "MELODIA " must land on the same board, and a seed that could carry
 * arbitrary text would be both a cross-client hashing hazard (case, whitespace,
 * unicode normalisation) and one more free-text field to sanitize server-side.
 * Normalizing at the edge means the seed the run plays is byte-identical to the
 * seed the board files it under.
 */

export const SEED_MAX_LEN = 24;

/** Prefix that marks a seed as a daily rather than a player's own text. */
const DAILY_PREFIX = 'DAILY-';

/**
 * Cleans player-entered (or stored) seed text into the canonical form, or null
 * if nothing usable is left. Null is the unseeded run — the same value every
 * mode that doesn't offer a seed passes, so there is exactly one "no seed".
 */
export function normalizeSeed(text) {
  if (typeof text !== 'string') return null;
  const cleaned = text
    .toUpperCase()
    .replace(/[^A-Z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, SEED_MAX_LEN)
    // A trailing dash can reappear after the slice.
    .replace(/-+$/, '');
  return cleaned.length > 0 ? cleaned : null;
}

/**
 * Today's daily seed, in UTC. UTC rather than local time so every player's
 * "today" is the same board — a daily that rolled over at local midnight would
 * give two neighbours in different zones different runs under one name, and the
 * board could not say which.
 */
export function dailySeed(now = new Date()) {
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  const d = String(now.getUTCDate()).padStart(2, '0');
  return `${DAILY_PREFIX}${y}-${m}-${d}`;
}

/** True for any daily seed (today's or an older one). */
export function isDailySeed(seed) {
  return typeof seed === 'string' && seed.startsWith(DAILY_PREFIX);
}

/** How a seed reads on screen — "Daily · 2026-07-26" for a daily, else itself. */
export function seedLabel(seed) {
  if (!seed) return '';
  return isDailySeed(seed) ? `Daily · ${seed.slice(DAILY_PREFIX.length)}` : seed;
}
